import fs from 'fs';
import path from 'path';
import { FBD_Veil } from '@rabbit/mixmaster/core';
import { FBD_Tenant_Public, TenantInfo } from '@rabbit/data/types';
import base from './veils/base.json';

// allowed partitions
const Partitions = {
  core: 1,
  cloud: 1,
};

export enum VeilKeys {
  CFG_TENANT_IS_MANUFACTURER = 'CFG_TENANT_IS_MANUFACTURER',
  CFG_COBRAND_NAME = 'CFG_COBRAND_NAME',
  CFG_COBRAND_FINISHING_MESSAGE = 'CFG_COBRAND_FINISHING_MESSAGE',
  CFG_COBRAND_EMAIL_MAIN_TEMPLATE = 'CFG_COBRAND_EMAIL_MAIN_TEMPLATE',
  CFG_COBRAND_EMAIL_SENDER = 'CFG_COBRAND_EMAIL_SENDER',
  CFG_COBRAND_EMAIL_TEMPLATE_LANGUAGE = 'CFG_COBRAND_EMAIL_TEMPLATE_LANGUAGE',
  EMAIL_SUBJECT_CLAIM_REGISTERED_WAITING_REVIEW = 'EMAIL_SUBJECT_CLAIM_REGISTERED_WAITING_REVIEW',
  EMAIL_SUBJECT_ITEM_REGISTERED_WITH_IWARRANTY = 'EMAIL_SUBJECT_ITEM_REGISTERED_WITH_IWARRANTY',
  EMAIL_SUBJECT_ACTIVATE_WARRANTY = 'EMAIL_SUBJECT_ACTIVATE_WARRANTY',
  EMAIL_SUBJECT_INVITED_TO_JOIN = 'EMAIL_SUBJECT_INVITED_TO_JOIN',
  CFG_DEFAULT_CURRENCY = 'CFG_DEFAULT_CURRENCY',
  CFG_USE_SHOPIFY_ORDER_NUMBER = 'CFG_USE_SHOPIFY_ORDER_NUMBER',
  TENANT_LINK = 'tenantLink',
  EMAIL_SUBJECT_STONE_REGISTRATION_THANK_YOU = 'EMAIL_SUBJECT_STONE_REGISTRATION_THANK_YOU',
  EMAIL_SUBJECT_VEHICLE_REGISTRATION = 'EMAIL_SUBJECT_VEHICLE_REGISTRATION',
  EMAIL_SUBJECT_EDIT_WARRANTY = 'EMAIL_SUBJECT_EDIT_WARRANTY',
  CFG_DEFAULT_PRODUCT_IMAGE = 'CFG_DEFAULT_PRODUCT_IMAGE',
  CFG_REGISTRATION_EMAIL_TEMPLATE = 'CFG_REGISTRATION_EMAIL_TEMPLATE',
  CFG_STANDARD_WARRANTY_OFFER_ENABLED = 'CFG_STANDARD_WARRANTY_OFFER_ENABLED',
  CFG_BUSINESS_COLOR = 'CFG_BUSINESS_COLOR',
  CFG_PARTNER_ONBOARDING_GUIDE = 'CFG_PARTNER_ONBOARDING_GUIDE',
  CFG_TENANT_WARRANTY_VOID_PERSONAS = 'CFG_TENANT_WARRANTY_VOID_PERSONAS',
  CFG_TENANT_PARTNER_CAN_VOID_WARRANTIES = 'CFG_TENANT_PARTNER_CAN_VOID_WARRANTIES',
  CFG_TENANT_OLIVE_LANDING_PAGE_ROUTE = 'CFG_TENANT_OLIVE_LANDING_PAGE_ROUTE',
  CFG_TENANT_OLIVE_LANDING_PAGE_INCLUDE_VENDABLE = 'CFG_TENANT_OLIVE_LANDING_PAGE_INCLUDE_VENDABLE',
  CFG_TENANT_MANAGE_MAKES_AND_MODELS = 'CFG_TENANT_MANAGE_MAKES_AND_MODELS',
  EMAIL_SUBJECT_PASSWORDLESS_LOGIN = 'CFG_EMAIL_SUBJECT_PASSWORDLESS_LOGIN',
  CFG_COBRAND_BRAND_DETAILS = 'CFG_COBRAND_BRAND_DETAILS',
  CFG_EMAIL_BRAND_SPECIFIC = 'CFG_EMAIL_BRAND_SPECIFIC',
  // Olive Dashboard: Message of the moment (tenant-specific banner text)
  CFG_MESSAGE_OF_THE_MOMENT = 'CFG_MESSAGE_OF_THE_MOMENT',
  CFG_LISTERS_REGISTRATION_MAIN_TEMPLATE = 'CFG_LISTERS_REGISTRATION_MAIN_TEMPLATE',
  CFG_LISTERS_REGISTRATION_EMAIL_TEMPLATE = 'CFG_LISTERS_REGISTRATION_EMAIL_TEMPLATE',
}

/**
 * Updates tenant.info field based on veil data (replicates migration logic)
 */
async function updateTenantInfoFromVeil(tenantLink: string, veilData: Record<string, string>): Promise<void> {
  try {
    console.log(`Updating tenant.info for ${tenantLink} from veil data...`);
    
    // Get current tenant document
    const tenantDoc = await FBD_Tenant_Public.get(tenantLink);
    if (!tenantDoc) {
      console.warn(`Tenant ${tenantLink} not found, skipping info update`);
      return;
    }

    // Merge base defaults with manufacturer-specific overrides (override only when key exists)
    const baseCore: Record<string, string> = (base as any)?.core ?? {};
    const veil = { ...baseCore, ...veilData } as Record<string, string>;

    const tenantInfo: TenantInfo = {
      logo: veil['CFG_COBRAND_LOGO'] || '',
      logoInverted: veil['CFG_COBRAND_LOGO_INVERTED'] || '',
      name: veil['CFG_COBRAND_NAME'] || tenantDoc.orgName || '',
      shortName: veil['CFG_COBRAND_SHORT_NAME'] || '',
      email: veil['CFG_COBRAND_EMAIL'] || tenantDoc.info?.email || '',
      phone: tenantDoc.info?.phone || '',
      address: veil['CFG_COBRAND_ADDRESSES'] ? JSON.parse(veil['CFG_COBRAND_ADDRESSES']) : tenantDoc.info?.address || [],
      country: veil['CFG_DEFAULT_COUNTRY'] || '',
      currency: veil['CFG_DEFAULT_CURRENCY'] || '',
      UPCPrefix: veil['CFG_COBRAND_UPC_PREFIX'] || '',
      productPlaceholder: veil['CFG_COBRAND_LANDING_IMAGE_PRODUCT_PLACEHOLDER'] || '',
      productSuggestions: veil['CFG_PRODUCT_SUGGESTIONS'] || '',
      warrantyInfoUrl: veil['CFG_WARRANTY_INFO_URL'] || '',
      shopifyUrl: veil['CFG_SHOPIFY_URL'] || '',
      claimsInfoUrl: veil['CFG_CLAIMS_INFO_URL'] || '',
      claimsInfoPhone: veil['CFG_CLAIMS_INFO_PHONE'] || '',
      claimsInfoEmail: veil['CFG_CLAIMS_INFO_EMAIL'] || '',
      authStrapline: veil['auth_strapline'] || '',
      sageAuthStrapline: veil['sage_auth_strapline'] || '',
      brandDetails: veil['CFG_COBRAND_BRAND_DETAILS'] || {},
      emailInfo: {
        emailSender: veil['CFG_COBRAND_EMAIL_SENDER'] || '',
        emailMainTemplate: veil['CFG_COBRAND_EMAIL_MAIN_TEMPLATE'] || '',
        emailTemplateLanguage: veil['CFG_COBRAND_EMAIL_TEMPLATE_LANGUAGE'] || '',
        emailFinishingMessage: veil['CFG_COBRAND_FINISHING_MESSAGE'] || '',
        subjects: {
          claimRegisteredWaitingReview: veil['EMAIL_SUBJECT_CLAIM_REGISTERED_WAITING_REVIEW'] || '',
          itemRegistered: veil['EMAIL_SUBJECT_ITEM_REGISTERED_WITH_IWARRANTY'] || '',
          activateWarranty: veil['EMAIL_SUBJECT_ACTIVATE_WARRANTY'] || '',
          invitedToJoin: veil['EMAIL_SUBJECT_INVITED_TO_JOIN'] || '',
          stoneRegistrationThankYou: veil['EMAIL_SUBJECT_STONE_REGISTRATION_THANK_YOU'] || '',
          vehicleRegistration: veil['EMAIL_SUBJECT_VEHICLE_REGISTRATION'] || '',
          editWarranty: veil['EMAIL_SUBJECT_EDIT_WARRANTY'] || '',
        },
      },
      allowedCurrencies: veil['CFG_ALLOWED_CURRENCIES'] ? JSON.parse(veil['CFG_ALLOWED_CURRENCIES']) : [],
      allowedCountries: veil['CFG_ALLOWED_COUNTRIES'] ? JSON.parse(veil['CFG_ALLOWED_COUNTRIES']) : [],
      messageOfTheMoment: veil['CFG_MESSAGE_OF_THE_MOMENT'] || '',
      serialNumberHelpText: veil['CFG_SERIAL_NUMBER_HELP_TEXT'] || '',
    };

    // Update tenant document with new info
    const updatedTenant = { ...tenantDoc, info: tenantInfo, tupdate: Date.now() };
    await FBD_Tenant_Public.set(updatedTenant);
    
    console.log(`Updated tenant.info for ${tenantLink}:`, {
      country: tenantInfo.country,
      currency: tenantInfo.currency,
      name: tenantInfo.name,
      email: tenantInfo.email,
      logo: tenantInfo.logo,
      logoInverted: tenantInfo.logoInverted
    });
  } catch (error) {
    console.error(`Error updating tenant.info for ${tenantLink}:`, error);
  }
}

export async function UploadAllVeilsToTheCloud() {
  // For now, purge all the veils

  // Go thru the veils folder and upload each veil
  // Get directory list of json files in ./veils

  const directoryPath = path.join(
    __dirname,
    '../../../libs/mixmaster/veils/src/veils'
  );

  console.debug('Scanning for veils in ' + directoryPath);

  const files = await fs.promises.readdir(directoryPath);
  const jsonFiles = files.filter((file) => path.extname(file) === '.json');

  jsonFiles.forEach((file) => {
    if (file.indexOf(' ') >= 0) {
      throw new Error(
        `Veil file ${file} contains spaces in its name. (no spaces allowed)`
      );
    }
  });

  // console.debug(`JSON files in ${directoryPath}:`);
  // console.debug(jsonFiles);

  for (const file of jsonFiles) {
    const filePath = path.join(directoryPath, file);
    const fileContent = await fs.promises.readFile(filePath, 'utf8');
    const veil = file.split('.json')[0];
    const body = JSON.parse(fileContent);

    console.debug(`Veil ${veil} loaded from ${filePath}`);
    // console.debug(body);

    // check the body for partitions
    for (const partition in body) {
      if ((Partitions as any)[partition] !== 1) {
        throw new Error(
          `Veil ${veil} contains partition ${partition} which is not allowed.`
        );
      }

      const content = body[partition];
      // Normalize known JSON blob keys to strings
      if (
        content['CFG_COBRAND_BRAND_DETAILS'] &&
        typeof content['CFG_COBRAND_BRAND_DETAILS'] !== 'string'
      ) {
        content['CFG_COBRAND_BRAND_DETAILS'] = JSON.stringify(
          content['CFG_COBRAND_BRAND_DETAILS']
        );
      }
      // Make sure all content is string only
      for (const key in content) {
        if (typeof content[key] !== 'string') {
          throw new Error(
            `Veil ${veil} contains partition ${partition} with key ${key} which is not a string.`
          );
        }
      }

      // Pump it into firebase
      const doc = FBD_Veil.empty();
      doc.docid = partition + ':' + veil;
      doc.kv = content;
      await FBD_Veil.set(doc);

      // If this is a manufacturer veil in the core partition, update tenant.info
      if (partition === 'core' && veil.startsWith('manufacturer_')) {
        const tenantLink = veil.replace('manufacturer_', '').toUpperCase();
        await updateTenantInfoFromVeil(tenantLink, content);
      }
    }
  }
  console.debug('== DONE ==');
  process.exit(0);
}

export { base };
